import dynamic from 'next/dynamic';
import React from 'react';

// Loading component for dynamic imports
const LoadingComponent = () => (
  <div className="flex items-center justify-center w-full h-full min-h-[200px]">
    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-gray-900"></div>
  </div>
);

// Error component for dynamic imports
const ErrorComponent = () => (
  <div className="flex items-center justify-center w-full h-full min-h-[200px] text-red-500">
    Failed to load component
  </div>
);

// Dynamic import options
const defaultOptions = {
  loading: LoadingComponent,
  error: ErrorComponent,
};

// Product-related components
export const DynamicProductGrid = dynamic(() => import('../components/sections/ProductGrid'), {
  ...defaultOptions,
  ssr: true,
});

export const DynamicDetailProductSection = dynamic(() => import('../components/products/DetailProductSection'), {
  ...defaultOptions,
  ssr: true,
});

export const DynamicAdditionalProductSection = dynamic(() => import('../components/products/AdditionalProductSection'), {
  ...defaultOptions,
  ssr: true,
});

export const DynamicImageProductCarouselSection = dynamic(() => import('../components/products/ImageProductCarouselSection'), {
  ...defaultOptions,
  ssr: true,
});

// UI Components
export const DynamicImageModal = dynamic(() => import('../components/ui/ImageModal'), {
  ...defaultOptions,
  ssr: false, // Modal doesn't need SSR
});

export const DynamicProductCarouselModal = dynamic(() => import('../components/ui/ProductCarouselModal'), {
  ...defaultOptions,
  ssr: false, // Modal doesn't need SSR
});

// Section Components
export const DynamicReviewsSection = dynamic(() => import('../components/sections/ReviewsSection'), {
  ...defaultOptions,
  ssr: true,
});

export const DynamicSocialMediaContact = dynamic(() => import('../components/sections/SocialMediaContact'), {
  ...defaultOptions,
  ssr: true,
});

// Sanity Studio (loaded only in /studio route)
export const DynamicSanityStudio = dynamic(() => import('../components/studio/SanityStudio'), {
  ...defaultOptions,
  ssr: false,
  loading: () => (
    <div className="flex items-center justify-center min-h-screen">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-accent-color"></div>
    </div>
  ),
});

// Dynamic PortableText for conditional rendering
export const DynamicPortableText = dynamic(() => import('../components/content/PortableText'), {
  ...defaultOptions,
  ssr: true,
});

// Dynamic PortableText with Components for rich content
export const DynamicPortableTextWithComponents = dynamic(() => 
  import('@portabletext/react').then(mod => ({
    default: ({ value, components, ...props }) => {
      const { PortableText } = mod;
      return <PortableText value={value} components={components} {...props} />;
    }
  })), {
  ...defaultOptions,
  ssr: true,
});

// Tab Content Components (only load when tab is active)
export const DynamicDescriptionTabContent = dynamic(() => import('../components/sections/DescriptionTabContent'), {
  ...defaultOptions,
  ssr: true,
});

export const DynamicFeaturesTabContent = dynamic(() => import('../components/sections/FeaturesTabContent'), {
  ...defaultOptions,
  ssr: true,
});

export const DynamicGeometryTabContent = dynamic(() => import('../components/sections/GeometryTabContent'), {
  ...defaultOptions,
  ssr: true,
});

export const DynamicCertificationsTabContent = dynamic(() => import('../components/sections/CertificationsTabContent'), {
  ...defaultOptions,
  ssr: true,
});

export const DynamicPicturesTabContent = dynamic(() => import('../components/sections/PicturesTabContent'), {
  ...defaultOptions,
  ssr: true,
}); 